import React, { useState } from 'react';
import { Layout, Edit2, Copy, Trash2, Star, AlertCircle, ChevronDown, ChevronRight, BarChart2, Eye, Plus } from 'lucide-react';
import {
  useLayoutsByObject,
  useDeleteLayout,
  useCloneLayout,
  useCreateLayout,
  getApplicationLabel,
  getTotalFieldCount,
  type PageLayout
} from '../../../services/queries/layoutQueries';
import type { CustomObject } from '../../../services/queries/objectQueries';
import { useFieldsByObject } from '../../../services/queries/fieldQueries';
import { useApplications } from '../../../services/queries/applicationQueries';
import { LayoutBuilder } from '../object-manager/LayoutBuilder';
import { DevModeBanner } from '../../common/DevModeBanner';
import { useToast } from '../../../contexts/ToastContext';

interface PageLayoutsTabProps {
  objectId: string;
  object: CustomObject;
  onNavigateToUIConfig?: () => void;
  onEditLayout?: (layoutId: string) => void;
}

/**
 * Page Layouts Tab
 *
 * Displays page layouts configured for a specific object with filtering by application.
 * Shows summary statistics including total layouts, app-specific layouts, and total fields.
 * Provides expandable previews of layout sections and fields, with actions to edit, clone, and delete layouts.
 * Links to the full UI Configuration editor for creating and editing layouts.
 *
 * Data Source: layoutQueries.ts (React Query with mock data)
 *
 * @component
 */
export const PageLayoutsTab: React.FC<PageLayoutsTabProps> = ({
  objectId,
  object,
  onNavigateToUIConfig,
  onEditLayout
}) => {
  const { success, error: showError } = useToast();
  const [applicationFilter, setApplicationFilter] = useState<string>('all');
  const [expandedLayoutId, setExpandedLayoutId] = useState<string | null>(null);
  const [layoutBuilderOpen, setLayoutBuilderOpen] = useState<PageLayout | null>(null);
  const [isNewLayoutModalOpen, setIsNewLayoutModalOpen] = useState(false);

  const { data: applicationOptions = [] } = useApplications();
  const { data: layouts, isLoading, error } = useLayoutsByObject(objectId, {
    applicationName: applicationFilter
  });

  const { data: fields } = useFieldsByObject(objectId);

  const deleteLayoutMutation = useDeleteLayout();
  const cloneLayoutMutation = useCloneLayout();
  const createLayoutMutation = useCreateLayout();

  const handleEdit = (layout: PageLayout) => {
    // Open Visual Builder for this layout
    setLayoutBuilderOpen(layout);
  };

  const handleDelete = async (layoutId: string, layoutName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${layoutName}"?`)) return;

    try {
      await deleteLayoutMutation.mutateAsync(layoutId);
      success('Page layout deleted successfully!', `${layoutName} has been permanently removed`);
    } catch (error: any) {
      showError('Failed to delete layout', error.message || 'Please try again');
    }
  };

  const handleClone = async (layoutId: string) => {
    try {
      await cloneLayoutMutation.mutateAsync(layoutId);
    } catch (error) {
      console.error('Failed to clone layout:', error);
    }
  };

  const handleCreateLayout = async (layoutName: string, description: string, application: string) => {
    try {
      const newLayout = await createLayoutMutation.mutateAsync({
        objectId: objectId,
        name: layoutName,
        description: description,
        applicationName: application,
        isDefault: false,
        sections: [
          {
            id: `section_${Date.now()}`,
            label: 'Information',
            order: 1,
            columns: 2,
            collapsible: true,
            collapsed: false,
            fields: []
          }
        ],
        assignedTo: ['all']
      });

      success('Page layout created successfully!', `${layoutName} is ready to configure`);
      setIsNewLayoutModalOpen(false);

      // Open Visual Builder for the new layout
      setLayoutBuilderOpen(newLayout);
    } catch (error: any) {
      showError('Failed to create layout', error.message || 'Please try again');
    }
  };

  // Calculate summary stats
  const totalLayouts = layouts?.length || 0;
  const totalFields = layouts?.reduce((sum, layout) => sum + getTotalFieldCount(layout), 0) || 0;
  const appSpecificLayouts = layouts?.filter(l => l.applicationName !== 'all').length || 0;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Page Layouts</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Configure how fields are arranged on {object.label} detail pages
            </p>
          </div>
        </div>

        {/* Development Mode Banner */}
        <DevModeBanner
          dataSource="mockDataManager.ts"
          itemCount={`${layouts?.length || 3} page layouts`}
          customMessage={`Development Mode: Showing ${layouts?.length || 3} page layouts persisted via mockDataManager.ts (localStorage). Changes survive page refresh.`}
          dismissible={true}
        />

        {/* Summary Stats */}
        {!isLoading && layouts && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <BarChart2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalLayouts}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Layouts</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Layout className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{appSpecificLayouts}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">App-Specific</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Star className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalFields}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Fields</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Application Filter and New Layout Button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by Application:</label>
            <select
              value={applicationFilter}
              onChange={(e) => setApplicationFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {applicationOptions.map((app) => (
                <option key={app.value} value={app.value}>
                  {app.label}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setIsNewLayoutModalOpen(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">New Page Layout</span>
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading layouts...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-900 dark:text-red-100">Error loading layouts</p>
            <p className="text-sm text-red-800 dark:text-red-300 mt-1">Please try again or contact support if the problem persists.</p>
          </div>
        </div>
      )}

      {/* Layouts List */}
      {!isLoading && !error && (
        <>
          {layouts && layouts.length > 0 ? (
            <div className="space-y-3">
              {layouts.map((layout) => (
                <LayoutCard
                  key={layout.id}
                  layout={layout}
                  isExpanded={expandedLayoutId === layout.id}
                  onToggleExpand={() => setExpandedLayoutId(expandedLayoutId === layout.id ? null : layout.id)}
                  onEdit={() => handleEdit(layout)}
                  onClone={() => handleClone(layout.id)}
                  onDelete={() => handleDelete(layout.id, layout.name)}
                  onOpenVisualBuilder={() => setLayoutBuilderOpen(layout)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <Layout className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No layouts found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {applicationFilter !== 'all'
                  ? `No layouts for ${getApplicationLabel(applicationFilter)}`
                  : 'No layouts available for this object'
                }
              </p>
              {applicationFilter !== 'all' && (
                <button
                  onClick={() => setApplicationFilter('all')}
                  className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium focus:outline-none focus:underline"
                >
                  View all layouts
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Layout Builder Modal */}
      {layoutBuilderOpen && fields && (
        <LayoutBuilder
          layout={layoutBuilderOpen}
          objectName={object.label}
          allFields={fields}
          onClose={() => setLayoutBuilderOpen(null)}
        />
      )}

      {/* New Page Layout Modal */}
      {isNewLayoutModalOpen && (
        <NewPageLayoutModal
          objectName={object.label}
          applicationOptions={applicationOptions}
          onClose={() => setIsNewLayoutModalOpen(false)}
          onCreate={handleCreateLayout}
          isCreating={createLayoutMutation.isPending}
        />
      )}
    </div>
  );
};

// Layout Card Component with Expandable Preview
interface LayoutCardProps {
  layout: PageLayout;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
  onOpenVisualBuilder: () => void;
}

const LayoutCard: React.FC<LayoutCardProps> = ({
  layout,
  isExpanded,
  onToggleExpand,
  onEdit,
  onClone,
  onDelete,
  onOpenVisualBuilder
}) => {
  const fieldCount = getTotalFieldCount(layout);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Card Header */}
      <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <button
              onClick={onToggleExpand}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors flex-shrink-0 mt-0.5"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            <Layout className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <button
                  onClick={onToggleExpand}
                  className="font-medium text-gray-900 dark:text-gray-100 hover:text-purple-600 dark:hover:text-purple-400 transition-colors focus:outline-none focus:underline text-left"
                >
                  {layout.name}
                </button>
                {layout.isDefault && (
                  <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded font-medium flex items-center gap-1">
                    <Star className="w-3 h-3" />
                    Default
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 flex-wrap">
                <span>{getApplicationLabel(layout.applicationName)}</span>
                <span>•</span>
                <span>{layout.sections.length} section{layout.sections.length !== 1 ? 's' : ''}</span>
                <span>•</span>
                <span>{fieldCount} field{fieldCount !== 1 ? 's' : ''}</span>
                {layout.description && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <span className="truncate max-w-md hidden sm:inline">{layout.description}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-auto">
            <button
              onClick={onOpenVisualBuilder}
              className="px-3 py-1.5 text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 flex items-center gap-1.5"
              title="Open Visual Builder"
            >
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium hidden sm:inline">Visual Builder</span>
            </button>
            <button
              onClick={onEdit}
              className="px-3 py-1.5 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 flex items-center gap-1.5"
              title="Edit in UI Configuration"
            >
              <Edit2 className="w-4 h-4" />
              <span className="text-sm font-medium hidden sm:inline">Edit</span>
            </button>
            <button
              onClick={onClone}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
              title="Clone Layout"
            >
              <Copy className="w-4 h-4" />
            </button>
            {!layout.isDefault && (
              <button
                onClick={onDelete}
                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                title="Delete Layout"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Preview */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Sections & Fields Preview
          </h4>

          {layout.sections.length > 0 ? (
            <div className="space-y-3">
              {layout.sections
                .sort((a, b) => a.order - b.order)
                .map((section) => (
                  <div
                    key={section.id}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">{section.label}</span>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>{section.columns} column{section.columns !== 1 ? 's' : ''}</span>
                        {section.collapsible && <span>• Collapsible</span>}
                      </div>
                    </div>

                    {section.fields.length > 0 ? (
                      <div className="space-y-1">
                        {section.fields
                          .sort((a, b) => a.order - b.order)
                          .slice(0, 5)
                          .map((field) => (
                            <div
                              key={field.id}
                              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 py-1"
                            >
                              <div className="w-1 h-1 rounded-full bg-gray-400"></div>
                              <span>{field.fieldLabel}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                (Col {field.column})
                              </span>
                              {field.required && (
                                <span className="text-xs text-red-600 dark:text-red-400">Required</span>
                              )}
                            </div>
                          ))}
                        {section.fields.length > 5 && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 italic py-1 pl-3">
                            and {section.fields.length - 5} more...
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">No fields in this section</p>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">No sections defined</p>
          )}
        </div>
      )}
    </div>
  );
};

// New Page Layout Modal Component
interface NewPageLayoutModalProps {
  objectName: string;
  applicationOptions: { value: string; label: string }[];
  onClose: () => void;
  onCreate: (name: string, description: string, application: string) => void;
  isCreating: boolean;
}

const NewPageLayoutModal: React.FC<NewPageLayoutModalProps> = ({
  objectName,
  applicationOptions,
  onClose,
  onCreate,
  isCreating
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [application, setApplication] = useState('all');
  const [errors, setErrors] = useState<{ name?: string }>({});

  const validateForm = () => {
    const newErrors: { name?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Layout name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    onCreate(name.trim(), description.trim(), application);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Create New Page Layout
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Define a new page layout for {objectName}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Layout Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Layout Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors({ ...errors, name: undefined });
                }}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="e.g., Credit Repair Client Layout"
                disabled={isCreating}
                autoFocus
              />
              {errors.name && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe the purpose of this layout..."
                rows={3}
                disabled={isCreating}
              />
            </div>

            {/* Application */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Application <span className="text-red-500">*</span>
              </label>
              <select
                value={application}
                onChange={(e) => setApplication(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isCreating}
              >
                {applicationOptions.map((app) => (
                  <option key={app.value} value={app.value}>
                    {app.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                This layout will be available for the selected application
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create & Open Visual Builder
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
